import React, { FC, useEffect, useState } from "react";
import {
  MuiBox,
  MuiIconButton,
  MuiModal,
  MuiTypography,
  RowProps,
} from "../../../../components";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
  useTransformEffect,
  useTransformInit,
} from "react-zoom-pan-pinch";
import { Tree, TreeNode } from "react-organizational-chart";
import { COLORS } from "../../../../styles/constants.style";
import {
  AccordionListWrapper,
  HierarchyModalWrapper,
  StyledNode,
} from "./ClientSearchResultHierarchy.style";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
} from "@mui/material";
import {
  ClientSearchResultHierarchyProps,
  HierarchyType,
  ViewType,
} from "../../modelsAndSchemas/Models";
import { ReactComponent as DragArrowUpIcon } from "../../../../assets/images/drag-arrow-up.svg";
import { ReactComponent as DragArrowDownIcon } from "../../../../assets/images/drag-arrow-down.svg";
import { ReactComponent as DragArrowLeftIcon } from "../../../../assets/images/drag-arrow-left.svg";
import { ReactComponent as DragArrowRightIcon } from "../../../../assets/images/drag-arrow-right.svg";
import { ReactComponent as ZoomInIcon } from "../../../../assets/images/zoom-in.svg";
import { ReactComponent as ZoomOutIcon } from "../../../../assets/images/zoom-out.svg";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { clientSearchSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ReactComponent as CollapseIcon } from "../../../../assets/images/collapse-icon.svg";
import { ReactComponent as ExpandIcon } from "../../../../assets/images/expand-icon.svg";
import {
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { ReactComponent as HierarchyIcon } from "../../../../assets/images/modal-hierarchy.svg";
import { ReactComponent as ListIcon } from "../../../../assets/images/modal-list.svg";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";

const getInitialLetter = (name: string) => {
  return name && name.length > 0 ? name[0].toUpperCase() : "";
};

const generateTree = (
  data: HierarchyType[],
  parentId: string | null = null,
): React.ReactNode[] => {
  // Find all nodes that have the given parentId
  const nodes = data.filter((node) => node.parentClientProfileId === parentId);

  // If no nodes found, return an empty array
  if (nodes.length === 0) return [];

  return nodes.map((node) => {
    // Get children for the current node
    const children = generateTree(data, node.id);

    // Render the current node and its children only if there are children
    if (isNotEmpty(children)) {
      return (
        <TreeNode
          key={node.id}
          label={
            <StyledNode>
              <ClientNode node={node} />
            </StyledNode>
          }
        >
          {children}
        </TreeNode>
      );
    }

    // If no children, just render the node without children (no lines)
    return (
      <TreeNode
        key={node.id}
        label={
          <StyledNode>
            <ClientNode node={node} />
          </StyledNode>
        }
      />
    );
  });
};

interface ClientNodeProps {
  node: HierarchyType;
}

const ClientNode: FC<ClientNodeProps> = ({ node }) => {
  return (
    <MuiBox className="container">
      <Avatar>
        <MuiTypography variant="h2SemiBold">
          {getInitialLetter(node.name)}
        </MuiTypography>
      </Avatar>
      <MuiBox className="details-wrapper">
        <MuiEllipseLabel text={node.name} labelTypoVariant="h2SemiBold" />
        <MuiTypography variant="h3Bold">{node.clientCode}</MuiTypography>
        <MuiBox className="location-container">
          <MuiEllipseLabel
            text={node.city}
            labelTypoVariant="h3Bold"
            labelTypoClassName="city-content"
          />
          {", "}
          <MuiTypography variant="h3Bold">{node.countryName}</MuiTypography>
        </MuiBox>
      </MuiBox>
    </MuiBox>
  );
};
const ClientSearchResultHierarchy: FC<ClientSearchResultHierarchyProps> = ({
  open,
  onClose,
  hierarchyData,
  clientName,
}) => {
  const { commonARIALabel } = useNavigationSiteCore();
  const [view, setView] = useState<ViewType>("hierarchy");

  const { fetchLabel, fetchMediaListImage, commonLabels } = useSiteCore();

  const [tableViewData, setTableViewData] = useState<any[]>([]);

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (hierarchyData) {
      const hierarchicalData = organizeDataHierarchy(hierarchyData); // Organize data into a tree structure
      setTableViewData(hierarchicalData);

      setExpandedRows(() => {
        const allRowIds = new Set<string>();
        const traverseRows = (rows: RowProps[]) => {
          rows.forEach((row) => {
            allRowIds.add(row.id);
            if (row.children && isNotEmpty(row.children)) {
              traverseRows(row.children); // Recursively add children to the set
            }
          });
        };
        traverseRows(hierarchicalData); // Populate the set with all row ids
        return allRowIds;
      });
    }
  }, [hierarchyData]);

  const Controls = () => {
    const { zoomIn, zoomOut, setTransform } = useControls();
    const [positionX, setPositionX] = useState(0);
    const [positionY, setPositionY] = useState(0);
    const [scale, setScale] = useState(1);

    useTransformInit(({ state, instance }) => {
      setScale(state.scale);
      setPositionX(state.positionX);
      setPositionY(state.positionY);
      return () => {
        // unmount
      };
    });

    useTransformEffect(({ state, instance }) => {
      setScale(state.scale);
      setPositionX(state.positionX);
      setPositionY(state.positionY);
      return () => {
        // unmount
      };
    });
    const step = 100;
    return (
      <MuiBox>
        <MuiBox className="zoom-buttons-container">
          <MuiIconButton
            aria-label={commonARIALabel.zoomIn}
            data-testid="zoom-in-button"
            onClick={() => zoomIn(0.5, 300, "easeOut")}
          >
            <ZoomInIcon />
          </MuiIconButton>
          <MuiIconButton
            aria-label={commonARIALabel.zoomOut}
            data-testid="zoom-out-button"
            onClick={() => zoomOut(0.5, 300, "easeOut")}
          >
            <ZoomOutIcon />
          </MuiIconButton>
        </MuiBox>
        <MuiBox className="drag-buttons-container">
          <MuiIconButton
            aria-label={commonARIALabel.panTowardsUp}
            data-testid="drag-up-button"
            onClick={() => {
              setTransform(positionX, positionY + step, scale, 300, "easeOut");
            }}
          >
            <DragArrowUpIcon />
          </MuiIconButton>
          <MuiBox className="horizontal-buttons-container">
            <MuiIconButton
              aria-label={commonARIALabel.panTowardsLeft}
              data-testid="drag-left-button"
              onClick={() => {
                setTransform(
                  positionX + step,
                  positionY,
                  scale,
                  300,
                  "easeOut",
                );
              }}
            >
              <DragArrowLeftIcon />
            </MuiIconButton>

            <MuiIconButton
              aria-label={commonARIALabel.panTowardsRight}
              data-testid="drag-right-button"
              onClick={() => {
                setTransform(
                  positionX - step,
                  positionY,
                  scale,
                  300,
                  "easeOut",
                );
              }}
            >
              <DragArrowRightIcon />
            </MuiIconButton>
          </MuiBox>

          <MuiIconButton
            aria-label={commonARIALabel.panTowardsDown}
            data-testid="drag-down-button"
            onClick={() => {
              setTransform(positionX, positionY - step, scale, 300, "easeOut");
            }}
          >
            <DragArrowDownIcon />
          </MuiIconButton>
        </MuiBox>
      </MuiBox>
    );
  };

  if (!hierarchyData) return null;

  const rootNodes = hierarchyData.filter((node) => !node.parentClientProfileId);

  const emptyHierarchyImageObject = fetchMediaListImage(
    clientSearchSiteCoreKeys.result,
    clientSearchSiteCoreKeys.noHierarchyImage,
    clientSearchSiteCoreKeys.smallImage,
  );

  const hierarchyContent = (
    <HierarchyModalWrapper>
      <TransformWrapper
        initialScale={0.5}
        minScale={0.4} // Limit zoom-out to 40%
        maxScale={2} // Limit zoom-in to 200%
        centerOnInit={true} // Center the content initially
      >
        {isNotEmpty(rootNodes) && <Controls />}

        {isNotEmpty(rootNodes) && (
          <TransformComponent>
            <MuiBox>
              <Tree
                lineWidth={"2px"}
                lineColor={COLORS.cirrus_black3}
                label={
                  <StyledNode>
                    <ClientNode node={rootNodes[0]} />
                  </StyledNode>
                }
              >
                {generateTree(hierarchyData, rootNodes[0].id)}
              </Tree>
            </MuiBox>
          </TransformComponent>
        )}
      </TransformWrapper>
    </HierarchyModalWrapper>
  );

  const emptyHierarchyContent = (
    <MuiBox
      className="no-hierarchy-container"
      tabIndex={0}
      aria-live="polite"
      aria-atomic="true"
    >
      {emptyHierarchyImageObject?.ImageUrl && (
        <img
          src={emptyHierarchyImageObject.ImageUrl}
          alt={emptyHierarchyImageObject.AltText}
          aria-hidden={!emptyHierarchyImageObject.AltText}
        />
      )}
      <MuiTypography variant="h4Medium">
        {fetchLabel(
          clientSearchSiteCoreKeys.result,
          clientSearchSiteCoreKeys.noHierarchyMessage,
        )}
      </MuiTypography>
      <MuiTypography variant="h4Medium">
        {fetchLabel(
          clientSearchSiteCoreKeys.result,
          clientSearchSiteCoreKeys.noHierarchyTagParent,
        )}
      </MuiTypography>
    </MuiBox>
  );

  const organizeDataHierarchy = (data: Record<string, any>[]) => {
    // Helper function to organize data into a hierarchy based on parentClientProfileId
    const map = new Map<string, any>();
    const roots: any[] = [];

    data.forEach((item) => {
      map.set(item.id, { ...item, children: [] });
    });

    data.forEach((item) => {
      if (item.parentClientProfileId) {
        const parent = map.get(item.parentClientProfileId);
        if (parent) {
          parent.children.push(map.get(item.id));
        }
      } else {
        roots.push(map.get(item.id));
      }
    });

    return roots;
  };

  const renderHeader = () => (
    <MuiBox className="header-row">
      <MuiBox className="first-column">
        <MuiTypography variant="h3" className="tableHeader nameHeader">
          {fetchLabel(
            clientSearchSiteCoreKeys.result,
            clientSearchSiteCoreKeys.companyNameHeader,
          )}
        </MuiTypography>
      </MuiBox>

      <MuiBox className="second-column">
        <MuiTypography variant="h3" className="tableHeader">
          {fetchLabel(
            clientSearchSiteCoreKeys.result,
            clientSearchSiteCoreKeys.clientCodeHeader,
          )}
        </MuiTypography>
      </MuiBox>
      <MuiBox className="third-column">
        <MuiTypography variant="h3" className="tableHeader">
          {fetchLabel(
            clientSearchSiteCoreKeys.result,
            clientSearchSiteCoreKeys.locationHeader,
          )}
        </MuiTypography>
      </MuiBox>
    </MuiBox>
  );

  // Handle the expansion and collapse of a row
  const toggleAccordion = (rowId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  // Recursive rendering of rows (main rows + children rows)
  const renderRow = (row: RowProps, depth: number = 0) => {
    const paddingLeft = depth * 40; // Indent child rows by 50px per depth level
    const isExpanded = expandedRows.has(row.id);
    const shouldHighlight =
      depth === 0 && row.name?.toLowerCase().includes("parent");

    return (
      <Accordion
        key={row.id}
        expanded={isExpanded}
        onChange={() => toggleAccordion(row.id)}
      >
        <AccordionSummary
          className="details-row"
          sx={{
            backgroundColor: shouldHighlight
              ? COLORS.primary_light_blue1
              : "transparent",
          }}
        >
          <MuiBox
            className="d-flex-center first-column"
            sx={{
              paddingLeft: `${paddingLeft}px`,
            }}
          >
            <MuiBox className="d-flex-center name-container">
              {row.children && isNotEmpty(row.children) && (
                <MuiIconButton onClick={() => toggleAccordion(row.id)}>
                  {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                </MuiIconButton>
              )}
              <MuiEllipseLabel
                text={row.name}
                labelTypoVariant="h3"
                labelTypoClassName={`${isEmpty(row.children) ? "empty-child-content" : ""}`}
              />
            </MuiBox>
          </MuiBox>

          <MuiBox className="d-flex-center second-column">
            <MuiTypography variant="h3">{row.clientCode}</MuiTypography>
          </MuiBox>

          <MuiBox className="d-flex-center third-column">
            <MuiEllipseLabel
              text={row.city}
              labelTypoVariant="h3"
              labelTypoClassName="city-content"
            />
            {", "}
            <MuiTypography variant="h3">{row.countryName}</MuiTypography>
          </MuiBox>
        </AccordionSummary>

        {row.children && isNotEmpty(row.children) && (
          <AccordionDetails>
            {row.children.map((child: RowProps) => renderRow(child, depth + 1))}
          </AccordionDetails>
        )}
      </Accordion>
    );
  };

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      title={replaceHashWithValue(
        fetchLabel(
          clientSearchSiteCoreKeys.result,
          clientSearchSiteCoreKeys.hierarchyPopUpHeading,
        ),
        {
          CLIENTNAME: clientName ?? "",
        },
      )}
      hasLargeContent
      primaryButtonTitle={""}
      secondaryButtonTitle={commonLabels.ok}
      onSecondaryButtonClick={onClose}
      headerChildren={
        isNotEmpty(rootNodes) && (
          <MuiBox className="view-buttons-container">
            <MuiIconButton
              aria-label={commonARIALabel.viewHierarchy}
              data-testid="hierarchy-view-button"
              onClick={() => setView("hierarchy")}
              className={`${view === "hierarchy" ? "active" : ""}`}
            >
              <HierarchyIcon />
            </MuiIconButton>
            <MuiIconButton
              aria-label={commonARIALabel.viewAsList}
              data-testid="list-view-button"
              onClick={() => setView("list")}
              className={`${view === "list" ? "active" : ""}`}
            >
              <ListIcon />
            </MuiIconButton>
          </MuiBox>
        )
      }
    >
      {isNotEmpty(rootNodes) ? (
        view === "hierarchy" ? (
          hierarchyContent
        ) : (
          <AccordionListWrapper>
            {renderHeader()}
            {tableViewData.map((row) => renderRow(row))}
          </AccordionListWrapper>
        )
      ) : (
        emptyHierarchyContent
      )}
    </MuiModal>
  );
};

export default ClientSearchResultHierarchy;
